/** Create a project: just a name. The backend derives the slug its URL uses. */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Dialog } from "@/components/ui/overlay";
import { errorText } from "@/components/ui/states";
import { openProject } from "@/lib/project-url";
import { useUiStore } from "@/stores/ui-store";
import { useCreateProject } from "./hooks";

/** Create a project from a name, then open it (also the first-project screen's). */
export function useCreateAndOpen() {
  const create = useCreateProject();
  const notify = useUiStore((s) => s.notify);
  const submit = (name: string) =>
    create.mutate(
      { name: name.trim() },
      {
        // A new project opens with a fresh page, like any project switch.
        onSuccess: (p) => openProject(p.slug),
        onError: (e) => notify(errorText(e, "Could not create the project."), "error"),
      },
    );
  return { submit, pending: create.isPending };
}

export function NewProjectDialog({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const create = useCreateAndOpen();
  return (
    <Dialog
      onSubmit={() => name.trim() && !create.pending && create.submit(name)}
      title="New project"
      subtitle="Holds its own agents, datasets, evaluators and experiments. Only people you add can see it. You'll be the owner."
      onClose={onClose}
      width={480}
      footer={
        <>
          <span className="flex-1" />
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={!name.trim() || create.pending}>
            Create project
          </Button>
        </>
      }
    >
      <Field label="Name" required htmlFor="project-name" hint="For example “CXR triage”.">
        <input
          id="project-name"
          className="inp"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
    </Dialog>
  );
}
